import React, { useState, MouseEvent, useEffect } from 'react';
import { Box, Typography, Container, Grid, Paper, ToggleButton, ToggleButtonGroup, Skeleton } from '@mui/material';
import { motion, useMotionValue } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Coins,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { gradients } from '../theme';
import FadeIn from './FadeIn';

interface MetalData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  isUp: boolean;
  data: number[];
  color: string;
}

const API_KEY = 'dca956f6acbbefb3aa46f96f8ecb32df';
const BASE_URL = 'https://api.metalpriceapi.com/v1/latest';

const FALLBACK_METALS: MetalData[] = [
  {
    symbol: 'Pt',
    name: 'Platinum',
    price: 2373.95,
    change: 1.85,
    isUp: true,
    data: [2320, 2345, 2330, 2362, 2350, 2385, 2373.95],
    color: '#E5E4E2',
  },
  {
    symbol: 'Pd',
    name: 'Palladium',
    price: 1892.77,
    change: -0.65,
    isUp: false,
    data: [1940, 1925, 1930, 1915, 1900, 1905, 1892.77],
    color: '#CED0DD',
  },
  {
    symbol: 'Rh',
    name: 'Rhodium',
    price: 9750.00,
    change: 3.20,
    isUp: true,
    data: [9400, 9450, 9550, 9620, 9680, 9720, 9750],
    color: '#F4F4F4',
  },
];

const Sparkline: React.FC<{ data: number[]; isUp: boolean }> = ({ data, isUp }) => {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const height = 60;
  const width = 200;

  const points = data
    .map((val, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((val - min) / range) * height;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={`gradient-${isUp ? 'up' : 'down'}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={isUp ? '#00f2ff' : '#ff4b2b'} stopOpacity="0.3" />
            <stop offset="100%" stopColor={isUp ? '#00f2ff' : '#ff4b2b'} stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path
          d={`M 0,${height} L ${points} L ${width},${height} Z`}
          fill={`url(#gradient-${isUp ? 'up' : 'down'})`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        />
        <motion.path
          d={`M ${points}`}
          fill="none"
          stroke={isUp ? '#00f2ff' : '#ff4b2b'}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
        />
      </svg>
    </Box>
  );
};

const PriceCard: React.FC<{
  metal: MetalData;
  currency: 'USD' | 'AED';
  index: number;
  aedRate: number;
}> = ({ metal, currency, index, aedRate }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: MouseEvent) => {
    const { currentTarget, clientX, clientY } = e;
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  };

  const currentPrice = currency === 'AED' ? metal.price * aedRate : metal.price;

  return (
    <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
      <FadeIn delay={index * 0.1} fullWidth>
        <Paper
          component={motion.div}
          onMouseMove={handleMouseMove}
          whileHover={{ y: -5 }}
          sx={{
            position: 'relative',
            p: 3.5,
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'rgba(0, 242, 255, 0.3)',
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
            },
          }}
        >
          <Box
            component={motion.div}
            sx={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(400px circle at var(--x) var(--y), rgba(0, 242, 255, 0.06), transparent 40%)`,
              opacity: 0,
              transition: 'opacity 0.3s',
              '&:hover': { opacity: 1 },
            }}
            style={{
              // @ts-ignore
              '--x': `${mouseX.get()}px`,
              // @ts-ignore
              '--y': `${mouseY.get()}px`,
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <Coins size={22} color={metal.color} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                    {metal.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'grey.500', fontWeight: 600 }}>
                    {metal.symbol} / {currency}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  backgroundColor: metal.isUp ? 'rgba(0, 242, 255, 0.1)' : 'rgba(255, 75, 43, 0.1)',
                  color: metal.isUp ? '#00f2ff' : '#ff4b2b',
                }}
              >
                {metal.isUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                  {metal.isUp ? '+' : ''}{metal.change}%
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="h3"
              sx={{
                color: 'white',
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.5rem' },
                letterSpacing: '-0.02em',
                mb: 1,
              }}
            >
              <Box component="span" sx={{ fontSize: '1.5rem', mr: 0.5, color: 'grey.500' }}>
                {currency === 'USD' ? '$' : 'AED'}
              </Box>
              {currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" sx={{ color: 'grey.600', fontWeight: 500 }}>
              Per Ounce / Real-time rate
            </Typography>
          </Box>

          <Sparkline data={metal.data} isUp={metal.isUp} />
        </Paper>
      </FadeIn>
    </Grid>
  );
};

const PGMPrices: React.FC = () => {
  const [currency, setCurrency] = useState<'USD' | 'AED'>('USD');
  const [metalData, setMetalData] = useState<MetalData[]>(FALLBACK_METALS);
  const [aedRate, setAedRate] = useState(3.673);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('Just now');

  const fetchPrices = async () => {
    try {
      setLoading(true);
      // Fetching Pt and Pd (XRH requires premium)
      const response = await fetch(`${BASE_URL}?api_key=${API_KEY}&base=USD&currencies=AED,XPT,XPD`);
      const data = await response.json();

      if (data.success) {
        setAedRate(data.rates.AED);

        const updatedMetals = FALLBACK_METALS.map(metal => {
          if (metal.symbol === 'Pt') {
            const newPrice = data.rates.USDXPT || metal.price;
            return {
              ...metal,
              price: newPrice,
              data: [...metal.data.slice(1), newPrice]
            };
          }
          if (metal.symbol === 'Pd') {
            const newPrice = data.rates.USDXPD || metal.price;
            return {
              ...metal,
              price: newPrice,
              data: [...metal.data.slice(1), newPrice]
            };
          }
          return metal;
        });

        setMetalData(updatedMetals);
        setLastUpdated(new Date().toLocaleTimeString());
        setError(null);
      } else {
        throw new Error(data.error?.message || 'Failed to fetch prices');
      }
    } catch (err: any) {
      console.error('Price Fetch Error:', err);
      setError('Live data temporary unavailable. Showing market averages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const handleCurrencyChange = (
    _event: React.MouseEvent<HTMLElement>,
    newCurrency: 'USD' | 'AED' | null,
  ) => {
    if (newCurrency !== null) {
      setCurrency(newCurrency);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'black',
        py: { xs: 12, md: 15 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decor */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.02) 1px, transparent 0)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(circle at 50% 50%, black 10%, transparent 80%)',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          right: '-5%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(0, 242, 255, 0.06) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(100px)',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <FadeIn direction="down">
          <Box sx={{ textAlign: 'left', mb: { xs: 8, md: 10 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 4 }}>
              <Box sx={{ maxWidth: 800 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#00f2ff',
                    fontWeight: 700,
                    letterSpacing: 3,
                    mb: 2,
                    display: 'block',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}
                >
                  Live Market Feed
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    ...gradients.primaryText,
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.03em',
                    mb: 3,
                  }}
                >
                  Real-Time PGM Rates.
                </Typography>
                <Typography
                  sx={{
                    color: 'grey.400',
                    fontSize: { xs: 18, md: 20 },
                    lineHeight: 1.6,
                    borderLeft: '2px solid rgba(0, 242, 255, 0.3)',
                    pl: 3,
                  }}
                >
                  Stay updated with global precious metal trends. We ensure absolute transparency with live, market-linked pricing updates.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: loading ? 'grey.500' : '#00f2ff' }}>
                  <Activity size={18} className={loading ? "" : "animate-pulse"} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                    {loading ? 'SYNCING...' : 'LIVE STATUS'}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={currency}
                  exclusive
                  onChange={handleCurrencyChange}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: '50px',
                    p: 0.5,
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    '& .MuiToggleButton-root': {
                      borderRadius: '50px',
                      px: 3,
                      py: 1,
                      color: 'grey.500',
                      border: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      transition: 'all 0.3s ease',
                      '&.Mui-selected': {
                        color: '#00f2ff',
                        backgroundColor: 'rgba(0, 242, 255, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 242, 255, 0.2)',
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      }
                    }
                  }}
                >
                  <ToggleButton value="USD">USD</ToggleButton>
                  <ToggleButton value="AED">AED</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Box>
        </FadeIn>

        <Grid container spacing={3}>
          {loading && metalData === FALLBACK_METALS ? (
            [1, 2, 3].map((i) => (
              <Grid key={i} size={{ xs: 12, md: 4 }}>
                <Skeleton
                  variant="rectangular"
                  height={300}
                  sx={{ borderRadius: 5, backgroundColor: 'rgba(255,255,255,0.02)' }}
                />
              </Grid>
            ))
          ) : (
            metalData.map((metal, index) => (
              <PriceCard
                key={metal.symbol}
                metal={metal}
                currency={currency}
                index={index}
                aedRate={aedRate}
              />
            ))
          )}
        </Grid>

        <FadeIn delay={0.4}>
          <Box
            sx={{
              mt: 6,
              p: 3,
              borderRadius: 4,
              backgroundColor: error ? 'rgba(255, 75, 43, 0.05)' : 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: error ? 'rgba(255, 75, 43, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
              textAlign: 'center'
            }}
          >
            {error ? <AlertCircle size={16} color="#ff4b2b" /> : <RefreshCw size={16} color="#00f2ff" />}
            <Typography sx={{ color: error ? '#ff4b2b' : 'grey.500', fontSize: '0.875rem', fontWeight: 500 }}>
              {error || `Rates are updated every 60 seconds from global commodities exchanges. Last update: ${lastUpdated}.`}
            </Typography>
          </Box>
        </FadeIn>
      </Container>
    </Box>
  );
};

export default PGMPrices;


